import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import type { CustomerOption } from "@cyh/contracts";
import { api } from "../../api";
import { useLanguage } from "../../i18n";

export function CustomerCombobox({ value, onChange }: { value: CustomerOption | null; onChange: (value: CustomerOption | null) => void }) {
  const { t } = useLanguage();
  const listId = useId();
  const requestId = useRef(0);
  const [query, setQuery] = useState(value?.name ?? "");
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => { setQuery(value?.name ?? ""); }, [value?.id]);
  useEffect(() => {
    if (!open) return;
    const currentRequest = ++requestId.current;
    const search = query === value?.name ? "" : query.trim();
    const timer = window.setTimeout(() => {
      setLoading(true); setFailed(false);
      void api.customerOptions(search, 20).then((rows) => {
        if (requestId.current !== currentRequest) return;
        setOptions(rows); setActiveIndex(0);
      }).catch(() => {
        if (requestId.current !== currentRequest) return;
        setOptions([]); setFailed(true);
      }).finally(() => { if (requestId.current === currentRequest) setLoading(false); });
    }, search ? 220 : 0);
    return () => window.clearTimeout(timer);
  }, [open, query, value?.id]);

  function select(option: CustomerOption) { onChange(option); setQuery(option.name); setOpen(false); }
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActiveIndex((index) => Math.min(index + 1, Math.max(0, options.length - 1))); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); }
    else if (event.key === "Enter" && open && options[activeIndex]) { event.preventDefault(); select(options[activeIndex]!); }
    else if (event.key === "Escape") setOpen(false);
  }

  return <div className="model-combobox customer-combobox" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <input className="model-combobox-input" required autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={listId} value={query} onFocus={(event) => { setOpen(true); if (value) event.currentTarget.select(); }} onChange={(event) => { setQuery(event.target.value); onChange(null); setOpen(true); }} onKeyDown={handleKeyDown} placeholder={t("输入客户名称、联系人、电话或地址", "Type customer, contact, phone, or address")} />
    {open && <div className="model-combobox-menu" id={listId} role="listbox">
      {loading ? <div className="model-combobox-state">{t("正在搜索…", "Searching…")}</div> : failed ? <div className="model-combobox-state error">{t("客户搜索失败，请重试", "Customer search failed. Try again.")}</div> : options.length ? options.map((option, index) => <button type="button" role="option" aria-selected={value?.id === option.id} className={index === activeIndex ? "active" : ""} key={option.id} onMouseEnter={() => setActiveIndex(index)} onMouseDown={(event) => { event.preventDefault(); select(option); }}><strong>{option.name}</strong>{(option.contactName || option.contactPhone) && <span>{[option.contactName, option.contactPhone].filter(Boolean).join(" · ")}</span>}{option.address && <small>{option.address}</small>}</button>) : <div className="model-combobox-state">{t("没有匹配的客户", "No matching customers")}</div>}
    </div>}
  </div>;
}
